import {lazy, type ReactNode} from 'react'
import {Link as RouterLink} from 'react-router-dom'
import Seo from './Seo'
import {relatedMarketingPages} from './seoConstants'
import {articleSchema} from './schema'
import {CtaSection, RelatedLinks} from './seoSections'
import LazyScene from './csgScenes/LazyScene'
import './SeoPage.css'

// Interactive scenes are code-split into their own chunks; each mounts (via
// LazyScene) only when scrolled near the viewport, showing a static placeholder
// until then — so the prerendered HTML holds no Three.js. All five are live.
const Scene1Body = lazy(() => import('./csgScenes/Scene1Body'))
const Scene2Cut = lazy(() => import('./csgScenes/Scene2Cut'))
const Scene3Features = lazy(() => import('./csgScenes/Scene3Features'))
const Scene4Material = lazy(() => import('./csgScenes/Scene4Material'))
const Scene5Batching = lazy(() => import('./csgScenes/Scene5Batching'))

// Long-form engineering article with 5 interactive Three.js/CSG demo slots.
// Unlike the data-driven GuideArticle, this page hand-composes its body because
// it embeds custom interactive components between prose blocks. The interactive
// scenes mount via <LazyScene> (code-split, viewport-gated).

const CANONICAL_PATH = '/guides/how-we-build-3d-device-mockups'
const TITLE = 'How We Generate 3D Device Mockups with Three.js and CSG — Ecranify Engineering'
const DESCRIPTION =
  'How Ecranify generates parametric 3D device mockups in the browser with three-bvh-csg, and how we made server-side rendering 30× faster.'

// External link (opens in a new tab). Internal Ecranify links use RouterLink.
const Ext = ({href, children}: {href: string; children: ReactNode}) => (
  <a className="seo-inline-link" href={href} target="_blank" rel="noopener noreferrer">
    {children}
  </a>
)

const EcranifyLink = ({children}: {children: ReactNode}) => (
  <RouterLink className="seo-inline-link" to="/">
    {children}
  </RouterLink>
)

const CsgArticlePage = () => {
  const related = relatedMarketingPages(CANONICAL_PATH)

  return (
    <div className="seo-root" data-prerender-ready>
      <Seo
        title={TITLE}
        description={DESCRIPTION}
        canonicalPath={CANONICAL_PATH}
        ogType="article"
        jsonLd={[articleSchema(TITLE, DESCRIPTION, CANONICAL_PATH)]}
      />
      <div className="seo-grid" aria-hidden />

      <main className="seo-page">
        <header className="seo-hero">
          <span className="seo-eyebrow">
            <span className="seo-eyebrow-dot" />
            Engineering
          </span>
          <h1 className="seo-h1">
            From a Rounded Box to Almost Any Phone on Earth: How We Build Device Mockups with Three.js and CSG
          </h1>
          <p className="seo-lede">
            Most mockup tools ship a fixed library of devices. When a new iPhone launches, you wait for someone to model
            it. When you need a tablet nobody bothered to add, you're stuck.
          </p>
          <p className="seo-lede">
            We took the opposite bet with <EcranifyLink>Ecranify</EcranifyLink>:{' '}
            <strong>no pre-made models at all</strong>. Every device — every button, speaker hole and antenna line — is
            generated procedurally live in your browser from a small JSON description. Want a phone with three buttons
            on the left and a USB-C port slightly off-center? Change a few numbers and the geometry rebuilds in front of
            you.
          </p>
          <div className="seo-meta">
            <span>Updated July 2026</span>
            <span>12 min read</span>
          </div>
        </header>

        <section className="seo-section">
          <p className="seo-p">
            This post is the story of how that works: first how we sculpt a device out of a single primitive, then how
            we light it — and then the part nobody warns you about: what happens when you have to render the same thing
            on a server with no GPU, and it takes 15 minutes.
          </p>
        </section>

        {/* ── Part 1 ───────────────────────────────────────── */}
        <section className="seo-section">
          <h2 className="seo-h2">Part 1: Sculpting a phone from one shape</h2>

          <h3 className="seo-h3">Step 0 — The toolbox</h3>
          <p className="seo-p">
            A quick word on what we're building this with. Browsers can draw hardware-accelerated 3D graphics into an
            ordinary <code>&lt;canvas&gt;</code> element through <strong>WebGL</strong> — a low-level API where you'd
            normally spend a week writing shader code just to display a lit cube. Nobody does that by hand; the de-facto
            standard wrapper is <Ext href="https://threejs.org">Three.js</Ext>, which gives you scenes, cameras, lights,
            materials and — most importantly for us — <em>geometry</em>: meshes described as lists of triangles. Since
            our app is React, we drive Three.js through <strong>React Three Fiber</strong>, which lets the 3D scene be
            composed declaratively like any other component tree. And for the actual sculpting we lean on one
            specialized library you'll meet in Step 2:{' '}
            <Ext href="https://github.com/gkjohnson/three-bvh-csg">three-bvh-csg</Ext>.
          </p>
          <p className="seo-p">That's the whole stack. Everything below is these tools, applied with patience.</p>

          <h3 className="seo-h3">Step 1 — A rounded rectangle, pulled into 3D</h3>
          <p className="seo-p">
            Everything starts embarrassingly simple: a 2D rounded rectangle, drawn as a Three.js <code>Shape</code> (a
            flat outline built from lines and arcs), sized in real millimeters — 163.4 × 78 mm for a current iPhone.
          </p>
          <p className="seo-p">
            To make it three-dimensional we <strong>extrude</strong> it — extrusion means taking a flat shape and
            stretching it along a straight path to give it depth, like pushing dough through a cookie cutter. 8.75 mm of
            depth later, we have a slab.
          </p>
          <p className="seo-p">
            A slab has razor-sharp edges, and no phone does. So the extrusion also applies a <strong>bevel</strong> —
            rounding or slanting off a hard edge so it catches light the way machined aluminum does. Three.js does both
            in one call: <code>ExtrudeGeometry</code> takes the flat shape, the depth and the bevel settings and hands
            back a finished mesh. The bevel radius is a parameter of the device JSON, so a boxy Galaxy and a
            soft-cornered iPad come from the same code path.
          </p>
          <p className="seo-p">
            How smooth those curves look is controlled by <strong>tessellation</strong> — the number of small flat
            triangles used to approximate a curved surface. More triangles, smoother curve, heavier mesh. Remember this
            word; it comes back to haunt us in Part 2.
          </p>
          <LazyScene
            component={Scene1Body}
            caption="Scene 1 — Sliders for corner radius, bevel and depth: watch a plain slab become a phone body."
          />

          <h3 className="seo-h3">Step 2 — Enter CSG: carving the front pocket</h3>
          <p className="seo-p">
            Here's where it stops being a box. <strong>CSG (Constructive Solid Geometry)</strong> means building complex
            solids by combining simple ones with boolean operations: <em>union</em> (glue two shapes together),{' '}
            <em>subtraction</em> (use one shape as a drill bit to remove material from another), and{' '}
            <em>intersection</em> (keep only the volume where both shapes overlap).
          </p>
          <p className="seo-p">
            This is where three-bvh-csg earns its keep: you wrap each mesh in a <code>Brush</code>, feed two brushes
            plus an operation (<code>SUBTRACTION</code>, <code>INTERSECTION</code>, <code>ADDITION</code>) into an{' '}
            <code>Evaluator</code>, and get a new mesh back. The library accelerates this with a <strong>BVH</strong> —
            a bounding volume hierarchy, essentially a spatial index that lets the algorithm quickly find which
            triangles of one mesh actually touch the other, instead of testing all of them.
          </p>
          <p className="seo-p">
            The first cut: the phone's face isn't flush with the body. There's a slightly recessed panel — the "pocket"
            the screen sits in. We build a second, slightly smaller rounded slab and <strong>intersect</strong> it with
            the body to carve that recess. Then the display itself is cut into the panel as its own inset rectangle.
          </p>
          <LazyScene
            component={Scene2Cut}
            caption="Scene 2 — A slab and a smaller box: a slider pushes the box into the slab; a checkbox hides it, leaving the hole it carved."
          />

          <h3 className="seo-h3">Step 3 — Features: an assortment of drill bits</h3>
          <p className="seo-p">
            Now the fun part. Every physical feature of a phone — side buttons, ports, the camera surround, the home
            button — is CSG with a purpose-built cutter. Let's walk through one in detail — the{' '}
            <strong>side button</strong> — because every other feature is a variation of the same recipe.
          </p>
          <p className="seo-p">
            A button is three shapes playing together. First, a rounded-rectangle cutter, slightly deeper than the
            button itself, is <strong>subtracted</strong> from the frame — that drills the slot. The slot's rim then
            gets a <strong>chamfer</strong> — a small angled transition edge, like the bevel's straight-edged cousin —
            so the opening doesn't look laser-cut sharp. Finally, a slightly smaller solid is placed back inside the
            slot as the button itself, standing a fraction of a millimeter proud of the frame, exactly like the real
            thing. Cut, soften, fill.
          </p>
          <p className="seo-p">
            Everything else follows the same pattern analogously: <strong>speaker grills</strong> are dozens of tiny
            cylindrical cutters — our iPad Pro 13″ has 58 of them — each backed by a dark inset disc;{' '}
            <strong>antenna lines</strong> are shallow grooves refilled with a strip of plastic-looking material; the{' '}
            <strong>charging port</strong> is a USB-C-shaped cut with a decorative insert with visible pins;{' '}
            <strong>SIM tray, mute switch and screws</strong> differ only in the cutter's shape and what goes back into
            the hole.
          </p>
          <p className="seo-p">
            The pattern is always: <em>subtract a cutter, sometimes fill with an insert</em>. The insert trick matters
            more than it sounds — a hole in a thin shell reads as fake immediately; a hole with something dark and
            metallic inside reads as hardware.
          </p>
          <p className="seo-p">
            Because every cutter's size and position comes from the device JSON (in millimeters from the device center),
            "adding a button" is data entry, not modeling.
          </p>
          <LazyScene
            component={Scene3Features}
            caption="Scene 3 — A plain box plus a picker of real device features (buttons, ports, home button, camera…): pick one and watch it carved into the face."
          />

          <h3 className="seo-h3">Step 4 — Front details that don't need drilling</h3>
          <p className="seo-p">
            Camera punch-holes, the Dynamic-Island-style cutout, the home indicator bar — these are visually flat, so
            cutting real geometry would be wasted triangles. They're rendered as <strong>decals</strong>: images
            projected flat onto a surface, like a sticker. A hidden projection plane spans the full front face; each
            decal is positioned on it in millimeters, same coordinate system as everything else.
          </p>
          <p className="seo-p">
            The one exception is the classic round home button — that one is a real CSG cut with a real concave button
            inside, because it has actual depth you can see at an angle.
          </p>

          <h3 className="seo-h3">Step 5 — The day the browser froze</h3>
          <p className="seo-p">
            For a while, all of this ran happily on the page itself. Then devices got more complex — more grills, more
            inserts, chamfered buttons — and one day, testing a heavier configuration, we noticed the editor visibly
            freezing the moment a slider moved.
          </p>
          <p className="seo-p">
            It wasn't a bug; it was arithmetic. JavaScript in the browser is <strong>single-threaded</strong>: the same
            thread runs your code, paints the page and responds to clicks. CSG on a ~30 000-triangle body with 80+
            boolean operations takes a couple of seconds even on a fast laptop — and while that computation runs,
            nothing else can: no scrolling, no clicks, no repaints. No spinner can save you; the spinner can't even
            animate, because animating it needs the very thread the computation is blocking.
          </p>
          <p className="seo-p">
            The fix was to get off that thread entirely: the whole build pipeline moved into a{' '}
            <strong>Web Worker</strong> — a genuine background thread the browser gives you, with the catch that it
            can't touch the page directly and communicates only by passing messages. The worker crunches geometry and
            streams the finished triangles back; the UI thread stays free to scroll, click and animate. And because a
            rebuild now takes visible time without blocking anything, we could add proper feedback: the feature you're
            editing shows up instantly as a translucent blue "ghost" — a deliberately primitive stand-in mesh (a simple
            box or cylinder, a few dozen triangles) that costs nothing to build on the UI thread. It holds the feature's
            exact place and size while the worker grinds through the real CSG, and gets thrown away the moment the
            finished geometry arrives.
          </p>
        </section>

        {/* ── Part 2 ───────────────────────────────────────── */}
        <section className="seo-section">
          <h2 className="seo-h2">Part 2: Making it look like a product photo</h2>
          <p className="seo-p">Geometry is half the illusion. The other half:</p>
          <ul className="seo-list seo-mt">
            <li>
              <strong>PBR materials</strong> — physically based rendering, where a surface is described by measurable
              properties (base color, metalness, roughness) instead of hand-tuned shading. Anodized aluminum is mostly a
              metalness/roughness recipe.
            </li>
            <li>
              <strong>Micro-detail via texture maps</strong>: the brushed-metal look of button and SIM-tray inserts
              comes from a <strong>roughness map</strong> and <strong>normal map</strong> — small images that vary the
              surface's shininess and apparent bumpiness per pixel without adding any triangles. Since CSG output has no
              natural texture coordinates, we generate them by <strong>box projection</strong> — projecting the texture
              onto the mesh from six directions like wrapping a gift box.
            </li>
            <li>
              <strong>HDRI environment</strong> — a high-dynamic-range panoramic image surrounding the scene, acting as
              both the reflection source and ambient light. This is what makes metal look like metal: it has something
              real to reflect.
            </li>
            <li>
              <strong>Directional lights + soft shadows</strong> — configurable studio lights casting variance-based
              soft shadows (VSM) onto an invisible <strong>shadow catcher</strong>: a transparent plane that displays
              only the shadow falling on it, so the device appears grounded on your background instead of floating.
            </li>
          </ul>
          <LazyScene
            component={Scene4Material}
            caption="Scene 4 — Material & light playground: a knot with sliders for metalness, roughness, base color, and light position + intensity."
          />
        </section>

        {/* ── Part 3 ───────────────────────────────────────── */}
        <section className="seo-section">
          <h2 className="seo-h2">Part 3: The render that isn't allowed to fail</h2>
          <p className="seo-p">
            Exports render client-side, on your GPU — instant and free for us. But exports cost credits, and clients run
            on <em>anything</em>: an old iPhone's browser gives a WebGL context a few hundred MB of GPU memory and kills
            the tab when you exceed it. Charging someone a credit and then crashing their tab is not a customer
            experience we were willing to ship.
          </p>
          <p className="seo-p">
            So every paid export has a <strong>server-side backup</strong>: the same React/Three.js scene loaded in
            headless Chrome on our server, which — having no GPU — renders through <strong>SwiftShader</strong>, a
            software rasterizer that emulates a GPU on the CPU. It produces pixel-identical output. It is also,
            unoptimized, brutally slow.
          </p>
          <p className="seo-p">
            First contact with reality: an iPad Pro 13″ — the one with 58 speaker grills —{' '}
            <strong>timed out at 15 minutes</strong> before producing a single frame. Here's how we got it to under half
            a minute.
          </p>

          <h3 className="seo-h3">Fix 1 — Stop drilling holes one at a time (~10× on geometry)</h3>
          <p className="seo-p">
            Our pipeline subtracted each cutter sequentially: cut grill 1 from the body, rebuild the body's BVH, cut
            grill 2, rebuild… With N features that's O(N²)-ish work, and the body mesh is the expensive operand. The
            fix: a <strong>collect phase</strong> gathers every cutter for a given base mesh, welds them into one
            compound cutter with Three.js's <code>mergeGeometries</code>, and performs{' '}
            <strong>a single subtraction</strong>. ~80 boolean operations became ~6. Geometry build on the worst device:{' '}
            <strong>~13 min → ~75 s</strong> on the server CPU (the same change made the client editor noticeably
            snappier too).
          </p>

          <h3 className="seo-h3">Fix 2 — Don't compute what nothing reads (~25%)</h3>
          <p className="seo-p">
            The CSG evaluator was tracking texture coordinates and material groups for every operation. But only two
            features (SIM tray, mute switch) actually sample textures from CSG output — everything else re-derives UVs
            by box projection afterwards. Two evaluator configs, one with UVs and one without, shaved roughly another
            quarter off.
          </p>

          <h3 className="seo-h3">Fix 3 — Texture diet</h3>
          <p className="seo-p">
            A speaker-grill detail texture shipped at 4500×4500 px (2.3 MB file, ~81 MB decoded on the GPU) — to be
            tiled onto discs about 2 mm wide. Who would ever do such a thing? Certainly not us. And yet, there it was,
            in our own repo, with our own names on the commit. Downscaled to 640×640: 43 kB, ~1.6 MB decoded, visually
            identical at any zoom a human would use. Background images got the same treatment: ~110 MB → ~13 MB across
            the catalog. On SwiftShader, "GPU memory" is RAM and texture uploads are CPU work, so this is speed, not
            just politeness.
          </p>

          <h3 className="seo-h3">Fix 4 — Tessellation, revisited</h3>
          <p className="seo-p">
            Remember tessellation from Step 1? The body's curved edges used 64 segments per curve and 32 per bevel —
            counts chosen when the mesh was only ever displayed, not used as a CSG operand 80 times. Halving them
            (32/16) is imperceptible on a 2-mm rounded edge but{' '}
            <strong>halves the triangle count fed into every boolean op</strong>. Small features (2-mm grill holes)
            dropped from 32 segments to 8 — a hole half a millimeter across does not need 32-sided smoothness. Gallery
            thumbnails go further and use a low-poly profile throughout.
          </p>

          <h3 className="seo-h3">Fix 5 — Shadows and supersampling honesty check</h3>
          <p className="seo-p">
            Exports used an 8192-px shadow map and 2× supersampling. Supersampling means rendering at double the target
            resolution and scaling down — the reason it looks better than rendering directly at the final size is that
            each final pixel becomes the average of four rendered ones, which smooths the jagged "staircase" edges (
            <strong>aliasing</strong>) that appear wherever a high-contrast geometry edge crosses a pixel grid. We
            A/B-compared against 2048 px shadows and 1× rendering on real exports and could not tell the difference —
            the soft VSM shadows blur away the extra shadow resolution, and the built-in MSAA anti-aliasing already
            handles the edges well enough at export sizes. On a software rasterizer, dropping both is a ~4× cut in raw
            pixel work.
          </p>

          <h3 className="seo-h3">Fix 6 — Don't render frames while building geometry</h3>
          <p className="seo-p">
            Subtle one: the render page was drawing frames at 60 fps (of an empty scene!) <em>while</em> the worker was
            doing CSG — on a server, both compete for the same CPU cores. The canvas now starts with its render loop{' '}
            <strong>off</strong>, builds geometry, then switches rendering on for the final frames. Free ~20–30% during
            the build phase.
          </p>

          <h3 className="seo-h3">Fix 7 — Never build the same device twice</h3>
          <p className="seo-p">
            The final realization: a device build is a pure function — same input JSON, same triangles. So the server
            now caches finished geometry, keyed by a hash of the full device configuration (content-addressed, stored as
            binary blobs). Any popular preset device is built exactly once; every subsequent render, thumbnail or retry{' '}
            <strong>skips Part 1 entirely</strong> and loads in ~2–3 s.
          </p>
          <LazyScene
            component={Scene5Batching}
            aspect="16 / 9"
            caption="Scene 5 — Batching race on the real iPhone 17 Pro Max (the default editor device): press Build! to generate the same phone three ways — sequential CSG one cutter at a time (watch the holes drill in one by one), all cutters merged into one subtraction (Fix 1), and the finished geometry loaded straight from cache (Fix 7). Each build runs in its own worker; the stopwatch under each phone freezes when that phone is fully built."
          />

          <h3 className="seo-h3">The scoreboard</h3>
          <p className="seo-p">
            The demo above runs on your own machine and isolates <strong>Fix 1 alone</strong> on a phone with ~25
            cutters, so you'll see roughly a 2–3× gap between the sequential and batched builds and a near-free cache
            load. This table is the iPad's ~80-cutter worst case with <em>every</em> fix stacked, where the wins
            compound — batching's edge grows with feature count, and Fixes 2–5 pile on top. The "Client editor rebuild"
            row is the closest apples-to-apples with the demo: same M-class CPU, heavier device.
          </p>
          <div className="seo-table-wrap seo-mt">
            <table className="seo-table">
              <caption>iPad Pro 13″, worst case</caption>
              <thead>
                <tr>
                  <th scope="col" className="seo-col-feature">
                    Stage
                  </th>
                  <th scope="col">Before</th>
                  <th scope="col">After</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="seo-col-feature">Geometry build (server CPU)</td>
                  <td>~13 min (timeout)</td>
                  <td>~75 s</td>
                </tr>
                <tr>
                  <td className="seo-col-feature">Full render incl. shadows + export</td>
                  <td>15+ min (failed)</td>
                  <td>
                    <strong>~25–30 s</strong>
                  </td>
                </tr>
                <tr>
                  <td className="seo-col-feature">Repeat render (geometry cached)</td>
                  <td>—</td>
                  <td>
                    <strong>~5 s</strong>
                  </td>
                </tr>
                <tr>
                  <td className="seo-col-feature">Client editor rebuild (M-class laptop)</td>
                  <td>~8 s</td>
                  <td>~1.5 s</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section className="seo-section">
          <h2 className="seo-h2">What we'd tell you if you're building something similar</h2>
          <p className="seo-p">
            Parametric CSG devices were the right call for us: new devices are JSON files, users can invent hardware
            that doesn't exist, and there is no model library to license or maintain. The costs are real but
            front-loaded — boolean geometry is expensive, so architect for batching from day one, keep tessellation as a
            quality <em>parameter</em> rather than a constant, and treat your worst-case device as a benchmark fixture.
          </p>
          <p className="seo-p">
            And if you just want the screenshots without building any of this: that's{' '}
            <EcranifyLink>Ecranify</EcranifyLink>. Every device in this post is configurable in the editor, in your
            browser, right now.
          </p>
        </section>

        <RelatedLinks title="Related guides & comparisons" links={related} />

        <CtaSection
          cta={{
            title: 'Skip the modeling. Ship the screenshots.',
            body: 'Every device in this article is configurable live in your browser — no signup, no model library. Start free and pay only when you export.',
          }}
        />
      </main>
    </div>
  )
}

export default CsgArticlePage
